import React, { useState } from "react";
import { motion } from "framer-motion";
import { User, Globe, Lock, Loader2, Crown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Profile() {
  const { t, lang, setLanguage } = useLang();
  const { user, setUser } = useAuth();

  const [name, setName] = useState(user?.name || "");
  const [language, setLang] = useState(user?.language || lang);
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/auth/profile", { name, language });
      setUser(data.user);
      setLanguage(language);
      toast.success(t("profile.saved"));
    } catch (err) { toast.error(apiError(err)); } finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      setCurrentPw(""); setNewPw("");
      toast.success(t("profile.pwChanged"));
    } catch (err) { toast.error(apiError(err)); } finally { setSavingPw(false); }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{t("profile.title")}</h1>
        <p className="mt-1 text-sm text-gray-600">{t("profile.sub")}</p>
      </div>

      <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={saveProfile}
        className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="profile-form">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-400"><User size={15} /> {t("profile.account")}</h3>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-semibold text-gray-500">{t("auth.email")}</label>
          <input value={user?.email || ""} disabled data-testid="profile-email"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500" />
          {user?.is_pro && (
            <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700"><Crown size={12} /> {t("common.premium")}</span>
          )}
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-semibold text-gray-500">{t("profile.name")}</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
        </div>

        <div className="mb-5">
          <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-500"><Globe size={13} /> {t("profile.language")}</label>
          <div className="flex gap-2">
            {[["es", "Español"], ["en", "English"]].map(([code, label]) => (
              <button key={code} type="button" onClick={() => setLang(code)} data-testid={`profile-lang-${code}`}
                className={`flex-1 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${language === code ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <button type="submit" disabled={savingProfile} data-testid="profile-save-button"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60">
          {savingProfile ? <Loader2 size={16} className="animate-spin" /> : null} {t("profile.save")}
        </button>
      </motion.form>

      <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} onSubmit={changePassword}
        className="mt-6 rounded-2xl border border-gray-200 bg-white p-6" data-testid="password-form">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-400"><Lock size={15} /> {t("profile.changePw")}</h3>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-semibold text-gray-500">{t("profile.currentPw")}</label>
          <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required data-testid="profile-current-pw"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
        </div>
        <div className="mb-5">
          <label className="mb-1.5 block text-xs font-semibold text-gray-500">{t("profile.newPw")}</label>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={6} data-testid="profile-new-pw"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
        </div>
        <button type="submit" disabled={savingPw} data-testid="profile-changepw-button"
          className="flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 py-3.5 text-sm font-bold text-gray-800 transition-colors hover:bg-gray-50 disabled:opacity-60">
          {savingPw ? <Loader2 size={16} className="animate-spin" /> : null} {t("profile.changePw")}
        </button>
      </motion.form>
    </div>
  );
}
